import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import axios from "axios";
import { Platform } from "react-native";
import { jwtDecode } from "jwt-decode";
import {
  LoginPayload,
  LoginResponse,
  LogoutPayload,
  LogoutResponse,
  Provider,
  RegisterPayload,
  RegisterResponse,
  ResetPasswordPayload,
  SetPasswordResponse,
  SSOExchangePayload,
  SubmitPayload,
  SubmitResponse,
} from "../../models/auth.models";
import apiClient from "./axios";
import { secureStorage } from "./secure-storage";
import { queryClient } from "@/src/config/react-query";

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const ACTIVE_ORG_KEY = "active_org_id";
const ORG_SELECTED_KEY = "org_selected";
const REMEMBER_ME_KEY = "remember_me";

const isWeb = Platform.OS === "web";

export type WebIdentity = Readonly<{ user_id: string; email: string }>;

let webIdentity: WebIdentity | null = null;
let webActiveOrgId: string | undefined;
let orgHydrated = false;
let identityInFlight: Promise<WebIdentity | null> | null = null;

export async function saveToken(token: string) {
  if (isWeb) return;
  try {
    await secureStorage.setItem(ACCESS_TOKEN_KEY, token);
  } catch (e) {
    console.error("Error saving token", e);
  }
}

export async function saveRefreshToken(token: string) {
  if (isWeb) return;
  try {
    await secureStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (e) {
    console.error("Error saving refresh token", e);
  }
}

export async function getToken() {
  if (isWeb) return null;
  try {
    return await secureStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (e) {
    console.error("Error getting token", e);
    return null;
  }
}

export async function getRefreshToken() {
  if (isWeb) return null;
  try {
    return await secureStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (e) {
    console.error("Error getting token", e);
    return null;
  }
}

async function hydrateActiveOrg() {
  if (!isWeb || orgHydrated) return;
  orgHydrated = true;
  try {
    webActiveOrgId = (await secureStorage.getItem(ACTIVE_ORG_KEY)) ?? undefined;
  } catch {
    webActiveOrgId = undefined;
  }
}

export async function setActiveOrg(orgId: string | undefined) {
  if (!isWeb) return;
  orgHydrated = true;
  webActiveOrgId = orgId;
  try {
    if (orgId) await secureStorage.setItem(ACTIVE_ORG_KEY, orgId);
    else await secureStorage.removeItem(ACTIVE_ORG_KEY);
  } catch (e) {
    console.error("Error persisting active org", e);
  }
}

export async function setOrgSelected(value: boolean) {
  try {
    if (value) await secureStorage.setItem(ORG_SELECTED_KEY, "true");
    else await secureStorage.removeItem(ORG_SELECTED_KEY);
  } catch (e) {
    console.error("Error persisting org selection", e);
  }
}

export async function getOrgSelected(): Promise<boolean> {
  try {
    return (await secureStorage.getItem(ORG_SELECTED_KEY)) === "true";
  } catch {
    return false;
  }
}

export async function getActiveOrg(): Promise<string | undefined> {
  if (isWeb) {
    await hydrateActiveOrg();
    return webActiveOrgId;
  }
  try {
    const stored = await secureStorage.getItem(ACTIVE_ORG_KEY);
    if (stored) return stored;
    const decoded = await decodeToken();
    return decoded?.org_id ?? undefined;
  } catch {
    return undefined;
  }
}

export async function setRememberMe(value: boolean) {
  try {
    await secureStorage.setItem(REMEMBER_ME_KEY, value ? "true" : "false");
  } catch (e) {
    console.error("Error persisting remember_me", e);
  }
}

export async function getRememberMe(): Promise<boolean> {
  try {
    return (await secureStorage.getItem(REMEMBER_ME_KEY)) === "true";
  } catch {
    return false;
  }
}

export function invalidateIdentity() {
  webIdentity = null;
  identityInFlight = null;
}

async function fetchAccount(): Promise<WebIdentity | null> {
  try {
    const res = await apiClient.get(
      process.env.EXPO_PUBLIC_GET_ACCOUNT as string,
      { _skipAuthRefresh: true } as any,
    );
    const body = res?.data ?? {};
    const account = body.data ?? body.account ?? body.user ?? body;
    const rawUserId = account.itemId ?? account.id ?? account.user_id;
    if (!rawUserId) return null;
    const email = account.email ?? account.emailAddress ?? "";
    return { user_id: String(rawUserId), email: String(email) };
  } catch {
    return null;
  }
}

async function ensureIdentity(): Promise<WebIdentity | null> {
  if (webIdentity) return webIdentity;
  if (identityInFlight) return identityInFlight;
  identityInFlight = fetchAccount()
    .then((id) => {
      webIdentity = id;
      return id;
    })
    .finally(() => {
      identityInFlight = null;
    });
  return identityInFlight;
}

export async function clearTokens() {
  if (isWeb) {
    webIdentity = null;
    identityInFlight = null;
    webActiveOrgId = undefined;
    orgHydrated = true;
    try {
      await secureStorage.multiRemove([
        ACTIVE_ORG_KEY,
        ORG_SELECTED_KEY,
        REMEMBER_ME_KEY,
      ]);
    } catch {}
    return;
  }
  try {
    await secureStorage.multiRemove([
      ACCESS_TOKEN_KEY,
      REFRESH_TOKEN_KEY,
      ORG_SELECTED_KEY,
      REMEMBER_ME_KEY,
    ]);
  } catch (e) {
    console.error("Error clearing tokens", e);
  }
}

export async function webServerLogout() {
  if (!isWeb) return;
  try {
    await axios.post(
      `${process.env.EXPO_PUBLIC_BLOCKS_BASE_URL}${process.env.EXPO_PUBLIC_LOGOUT}`,
      { refreshToken: "" },
      {
        withCredentials: true,
        headers: {
          "x-blocks-key": process.env.EXPO_PUBLIC_X_BLOCKS_KEY as string,
        },
      },
    );
  } catch {}
}

const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (!exp) return false;
    return exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

export const isAuthenticated = async (): Promise<boolean> => {
  if (isWeb) return !!(await ensureIdentity());
  const accessToken = await getToken();
  if (accessToken && !isTokenExpired(accessToken)) return true;
  const refreshToken = await getRefreshToken();
  return !!refreshToken;
};

export const decodeToken = async () => {
  if (isWeb) {
    await hydrateActiveOrg();
    if (!webIdentity && !webActiveOrgId) return null;
    return {
      user_id: webIdentity?.user_id,
      email: webIdentity?.email,
      org_id: webActiveOrgId,
      roles: undefined,
    } as any;
  }
  const token = await getToken();
  if (!token) return null;
  try {
    return jwtDecode(token) as any;
  } catch (e) {
    console.error("Error decoding token", e);
    return null;
  }
};
export const useLoginMutation = (
  options?: Omit<
    UseMutationOptions<LoginResponse, unknown, LoginPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<LoginResponse, unknown, LoginPayload>({
    mutationFn: (payload) =>
      apiClient
        .post(
          process.env.EXPO_PUBLIC_OAUTH_TOKEN as string,
          new URLSearchParams({
            username: payload.email,
            password: payload.password,
            grant_type: "password",
            remember_me: String(payload.rememberMe ?? false),
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        )
        .then((res) => {
          return res.data;
        }),
    ...options,
  });
};

export const useSSOExchangeMutation = (
  options?: Omit<
    UseMutationOptions<LoginResponse, unknown, SSOExchangePayload>,
    "mutationFn"
  >,
) => {
  return useMutation<LoginResponse, unknown, SSOExchangePayload>({
    mutationFn: (payload) =>
      apiClient
        .postForm(process.env.EXPO_PUBLIC_OAUTH_TOKEN as string, {
          ...payload,
          grant_type: "social",
        })
        .then((res) => res.data),
    ...options,
  });
};
// TO-DO: Rename this
export const useSubmitMutation = (
  options?: Omit<
    UseMutationOptions<SubmitResponse, unknown, SubmitPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<SubmitResponse, unknown, SubmitPayload>({
    mutationFn: (payload) =>
      apiClient
        .post(process.env.EXPO_PUBLIC_RECOVER as string, {
          email: payload.email,
          mailPurpose: process.env.EXPO_PUBLIC_RECOVER_ACCOUNT as string,
          projectKey: process.env.EXPO_PUBLIC_X_BLOCKS_KEY as string,
        })
        .then((res) => res.data),
    ...options,
  });
};

export const useResetPasswordMutation = (
  options?: Omit<
    UseMutationOptions<SetPasswordResponse, unknown, ResetPasswordPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<SetPasswordResponse, unknown, ResetPasswordPayload>({
    mutationFn: (payload) =>
      apiClient
        .post(process.env.EXPO_PUBLIC_RESET_PASSWORD as string, {
          password: payload.password,
          code: payload.code,
          logoutFromAllDevices: true,
          projectKey: process.env.EXPO_PUBLIC_X_BLOCKS_KEY as string,
        })
        .then((res) => res.data),
    ...options,
  });
};

export const useSetPasswordMutation = (
  options?: Omit<
    UseMutationOptions<SetPasswordResponse, unknown, ResetPasswordPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<SetPasswordResponse, unknown, ResetPasswordPayload>({
    mutationFn: (payload) =>
      apiClient
        .post(process.env.EXPO_PUBLIC_ACTIVATE_ACCOUNT as string, {
          password: payload.password,
          code: payload.code,
          projectKey: process.env.EXPO_PUBLIC_X_BLOCKS_KEY as string,
        })
        .then((res) => res.data),
    ...options,
  });
};

export const useRegisterMutation = (
  options?: Omit<
    UseMutationOptions<RegisterResponse, unknown, RegisterPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<RegisterResponse, unknown, RegisterPayload>({
    mutationFn: (payload) =>
      apiClient
        .post("/auth/request_email", {
          email: payload.email,
          firstName: payload.firstName,
          lastName: payload.lastName,
        })
        .then((res) => res.data),
    ...options,
  });
};

export const useLogoutMutation = (
  options?: Omit<
    UseMutationOptions<LogoutResponse, unknown, LogoutPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<LogoutResponse, unknown, LogoutPayload>({
    mutationFn: (payload) =>
      apiClient
        .post(
          process.env.EXPO_PUBLIC_LOGOUT as string,
          { refreshToken: payload.refreshToken ?? "" },
          {
            headers: {
              Authorization: `Bearer ${payload.accessToken}`,
            },
          },
        )
        .then((res) => res.data),
    onSuccess: () => {
      clearTokens();
      queryClient.clear();
    },
    ...options,
  });
};

export const getSSOUrl = async (provider: Provider): Promise<string> => {
  const response = await apiClient
    .post(
      process.env.EXPO_PUBLIC_GET_SOCIAL_LOGIN_ENDPOINT as string,
      JSON.stringify({
        provider,
        sendAsResponse: true,
        audience: process.env.EXPO_PUBLIC_AUDIENCE,
      }),
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
    .then((res) => {
      return res;
    });
  return response.data.providerUrl;
};

export async function storeTokens(accessToken: string, refreshToken: string) {
  await Promise.all([saveToken(accessToken), saveRefreshToken(refreshToken)]);
}

export async function refreshTokens(
  refreshToken?: string,
): Promise<{ access_token: string; refresh_token: string }> {
  const token = refreshToken || (await getRefreshToken()) || "";
  const orgId = await getActiveOrg();
  const body = new URLSearchParams({ grant_type: "refresh_token" });
  if (token) body.set("refresh_token", token);
  if (orgId) body.set("org_id", orgId);

  const res = await axios.post(
    `${process.env.EXPO_PUBLIC_BLOCKS_BASE_URL}${process.env.EXPO_PUBLIC_REFRESH_TOKEN}`,
    body,
    {
      withCredentials: true,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "x-blocks-key": process.env.EXPO_PUBLIC_X_BLOCKS_KEY as string,
      },
    },
  );
  return res.data;
}

export async function switchOrganization(
  orgId: string,
  refreshToken: string,
): Promise<{ access_token: string; refresh_token: string }> {
  const token = refreshToken || (await getRefreshToken()) || "";
  const rememberMe = await getRememberMe();
  const body = isWeb
    ? `grant_type=switch_organization&org_id=${encodeURIComponent(orgId)}&remember_me=${rememberMe}`
    : `refresh_token=${encodeURIComponent(token)}&grant_type=switch_organization&org_id=${encodeURIComponent(orgId)}&remember_me=${rememberMe}`;
  const res = await apiClient.post("/idp/v1/Authentication/Token", body, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
  const data = res.data;
  if (isWeb) {
    await setActiveOrg(orgId);
    invalidateIdentity();
  } else {
    await saveToken(data.access_token);
    await saveRefreshToken(data.refresh_token);
  }
  await setOrgSelected(true);
  return data;
}
